<template>
  <div class="create-payout-to-card page--create-payout-to-card">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('payouts.to-card.create-page.title') }}
        </div>
      </div>
    </div>
    <div class="create-payout-to-card__content">
      <div class="create-payout-to-card__form">
        <div class="create-payout-to-card__form-loader" v-if="isPayoutCreating">
          <loader></loader>
        </div>
        <div class="create-payout-to-card__form-fields">
          <div class="create-payout-to-card__form-field">
            <text-input
              v-model:value="payoutToCardData.pan"
              :mask="'XXXX XXXX XXXX XXXX'"
              :title="$t('payouts.to-card.create-page.fields.pan')"
              :placeholder="
                $t('payouts.to-card.create-page.fields.pan-placeholder')
              "
            ></text-input>
          </div>
          <div class="create-payout-to-card__form-field">
            <text-input
              v-model:value="payoutToCardData.lastName"
              :title="$t('payouts.to-card.create-page.fields.last-name')"
              :placeholder="
                $t('payouts.to-card.create-page.fields.last-name-placeholder')
              "
            ></text-input>
          </div>
          <div class="create-payout-to-card__form-field">
            <text-input
              v-model:value="payoutToCardData.firstName"
              :title="$t('payouts.to-card.create-page.fields.first-name')"
              :placeholder="
                $t('payouts.to-card.create-page.fields.first-name-placeholder')
              "
            ></text-input>
          </div>
          <div class="create-payout-to-card__form-field">
            <text-input
              v-model:value="payoutToCardData.middleName"
              :title="$t('payouts.to-card.create-page.fields.middle-name')"
              :placeholder="
                $t('payouts.to-card.create-page.fields.middle-name-placeholder')
              "
            ></text-input>
          </div>
          <div class="create-payout-to-card__form-field">
            <text-input
              v-model:value="payoutToCardData.ipn"
              :mask="'NNNNNNNNNN'"
              :title="$t('payouts.to-card.create-page.fields.ipn')"
              :placeholder="$t('payouts.to-card.create-page.fields.ipn')"
            ></text-input>
          </div>
          <div class="create-payout-to-card__form-field">
            <div class="create-payout-to-card__form-field--row">
              <div class="create-payout-to-card__form-field-small">
                <custom-select
                  v-model:value="payoutToCardData.currency"
                  :title="$t('payouts.to-card.create-page.fields.currency')"
                  value-key="id"
                  :options="$options.CURRENCY_OPTIONS"
                  mode="big"
                  :has-default-value="false"
                ></custom-select>
              </div>
              <div class="create-payout-to-card__form-field-big">
                <text-input
                  v-model:value="payoutToCardData.sum"
                  :mask="$options.NUMBER_MASK"
                  :placeholder="
                    $t('payouts.to-card.create-page.fields.sum-placeholder')
                  "
                  :title="$t('payouts.to-card.create-page.fields.sum')"
                ></text-input>
              </div>
            </div>

            <div class="create-payout-to-card__form-field-hint">
              {{
                $t('payouts.to-card.create-page.fields.sum-hint', {
                  minSum: $options.PAYOUT_OPTIONS.minSum,
                  maxSum: $options.PAYOUT_OPTIONS.maxSum,
                  currency: $options.PAYOUT_OPTIONS.currency,
                })
              }}
            </div>
          </div>
          <div class="create-payout-to-card__form-field">
            <text-area-input
              v-model:value="payoutToCardData.comment"
              :title="$t('payouts.to-card.create-page.fields.comment')"
            ></text-area-input>
            <div class="create-payout-to-card__form-field-hint">
              {{ $t('payouts.to-card.create-page.fields.comment-placeholder') }}
            </div>
          </div>
        </div>
        <div class="create-payout-to-card__form-actions">
          <primary-button
            class="create-payout-to-card__form-action"
            :disabled="
              !isCreatePayoutButtonActive || v$.$silentErrors.length > 0
            "
            :label="$t('payouts.to-card.create-page.actions.create')"
            @click="createPayoutToCard"
          ></primary-button>
        </div>
      </div>
    </div>
    <success-notification
      ref="createPayoutToCardSuccessNotification"
      :text="$t('payouts.to-card.create-page.notifications.success')"
    ></success-notification>
    <success-notification
      ref="createPayoutToCardInProgressNotification"
      :text="$t('payouts.to-card.create-page.notifications.in-progress')"
    ></success-notification>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import TextAreaInput from '@vue-app/components/base/input/TextAreaInput.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import useVuelidate from '@vuelidate/core'
import { minLength, maxLength, required } from '@vuelidate/validators'

export default {
  name: 'CreatePayoutToCard',
  props: ['id'],
  components: {
    Loader,
    TextAreaInput,
    SuccessNotification,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  setup: () => ({
    v$: useVuelidate(),
  }),
  data() {
    return {
      payoutToCardData: {
        currency: 'uah',
        sum: '',
        comment: '',
        pan: '',
        firstName: '',
        lastName: '',
      },
      isPayoutCreating: false,
      payoutsTimeout: null,
      payoutsSeconds: 0,
    }
  },
  validations() {
    return {
      payoutToCardData: {
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        middleName: {
          required,
        },
        ipn: {
          required,
          minLength: minLength(8),
          maxLength: maxLength(12),
        },
      },
    }
  },
  NUMBER_MASK: createNumberMask({
    prefix: '',
    allowDecimal: true,
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ' ',
    allowNegative: false,
    decimalLimit: 2,
  }),

  CURRENCY_OPTIONS: [
    {
      id: 'uah',
      name: 'UAH',
    },
  ],

  PAYOUT_OPTIONS: {
    minSum: 1,
    maxSum: 29999,
    currency: 'грн',
  },

  computed: {
    isCreatePayoutButtonActive() {
      if (this.isPayoutCreating) {
        return false
      }

      return (
        parseFloat(this.payoutToCardData.sum.replace(/\s+/g, '')) >=
          this.$options.PAYOUT_OPTIONS.minSum &&
        parseFloat(this.payoutToCardData.sum.replace(/\s+/g, '')) <=
          this.$options.PAYOUT_OPTIONS.maxSum &&
        this.payoutToCardData.pan.replace(/\s+/g, '').length === 16
      )
    },
    backLinkRoute() {
      return RouteHelper.getRoute('payouts', { companyId: this.id })
    },
  },

  methods: {
    async createPayoutToCard() {
      if (this.isPayoutCreating) {
        return false
      }

      let isValid = await this.v$.$validate()

      if (!isValid) {
        return false
      }

      this.isPayoutCreating = true

      try {
        CompaniesApi.createPayoutToCard(this.id, this.payoutToCardData)
          .then()
          .finally(() => {
            this.isPayoutCreating = false
          })

        await this.$refs.createPayoutToCardInProgressNotification.show()
        await this.$router.push(
          RouteHelper.getRoute(`payouts`, {
            companyId: this.id,
          })
        )
      } catch (e) {
        console.error(e)
      } finally {
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--create-payout-to-card {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.create-payout-to-card {
  &__content {
    display: grid;
    grid-template-columns: 390px 1fr;
    column-gap: 15px;
  }

  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 60px 50px 74px;
    width: 100%;
    max-width: 390px;
    position: relative;

    &-loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      background: rgba(#ffffff, 0.7);
      z-index: 999;
    }

    &-fields {
      margin-bottom: 40px;
    }

    &-field {
      margin-bottom: 25px;

      &-hint {
        width: 100%;
        font-weight: 500;
        font-size: 10px;
        line-height: 14px;
        color: #b9c4d8;
        margin-top: 5px;
        flex-wrap: wrap;
      }

      &--row {
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        width: calc(100% - 121px);
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }

  &__history {
    &-title {
      background: #ffffff;
      border-radius: 8px;
      padding: 7px 28px 8px;
      font-weight: 600;
      font-size: 16px;
      line-height: 28px;
      color: #465a8b;
    }

    &-table {
      margin-top: 15px;
    }
  }
}
</style>
